/**
 * 코스 별점 — 따라해 본 사람만.
 *
 * 왜 자격을 두나
 *   TourAPI 인기순은 조회수 기준입니다. 그래서 제주 1위가 가새기오름이고
 *   867건을 훑어도 성산일출봉이 없습니다. 보기만 한 사람의 평가가 쌓이면
 *   우리도 같은 길을 갑니다. 서버가 `travel_course_copy` 기록이 없으면
 *   403 으로 막고, 화면은 자격이 없으면 이 영역을 아예 그리지 않습니다.
 *
 * 왜 후기 글을 필수로 안 받나
 *   별점 한 번 누르는 것과 글 쓰는 것은 비용 차이가 큽니다. 글을 필수로
 *   두면 별점 자체가 안 모입니다. 별점만으로도 평균은 쌓입니다.
 *
 * 이미 남긴 별점이 있으면 그 값에서 시작합니다 — 고치는 건 정상입니다.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { Button, Section, SectionHeader, StarInput, TextArea } from 'components/ds';
import { CourseReview, useCourseReview } from 'hooks/useTravelCourse';
import styles from './CourseRatingSection.module.css';

interface Props {
    courseId: string;
    /** 이미 남긴 별점 — 있으면 그 값으로 시작합니다 */
    myReview?: CourseReview | null;
    /** 별점이 들어가면 코스의 평균이 바뀌므로 상세를 다시 읽습니다 */
    onSaved?: () => void;
}

const MAX_CONTENT = 500;

export const CourseRatingSection = ({ courseId, myReview, onSaved }: Props) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const { submit, busy } = useCourseReview(courseId);

    const [rating, setRating] = useState(myReview?.rating || 0);
    const [content, setContent] = useState(myReview?.content || '');

    const onSubmit = async () => {
        if (rating < 1) {
            showAlert({ message: t('course.ratePick') });
            return;
        }
        const res = await submit(rating, content.trim() || undefined);
        if (!res.ok) {
            showAlert({ message: res.message || t('course.rateFailed') });
            return;
        }
        showAlert({ message: t('course.rated') });
        onSaved?.();
    };

    return (
        <Section divided>
            <SectionHeader title={t('course.rateTitle')} sub={t('course.rateSub')} />

            <div className={styles.stars}>
                <StarInput
                    value={rating}
                    onChange={setRating}
                    labels={[
                        t('course.rate1'), t('course.rate2'), t('course.rate3'),
                        t('course.rate4'), t('course.rate5'),
                    ]}
                />
            </div>

            {/* label 이 있어야 optionalText 가 그려집니다 (ds/Field) —
               글은 선택이라는 걸 라벨에서 밝혀 둡니다. */}
            <TextArea
                label={t('course.rateContentLabel')}
                optionalText={t('course.optional')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('course.ratePlaceholder')}
                maxLength={MAX_CONTENT}
                rows={3}
                showCount
            />

            <div className={styles.footer}>
                <Button variant="primary" size="lg" block disabled={busy} onClick={onSubmit}>
                    {myReview ? t('course.rateUpdate') : t('course.rateSubmit')}
                </Button>
            </div>
        </Section>
    );
};
